import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { CalendarRange, Trophy, AlertCircle, AlertTriangle, Info, Crown } from 'lucide-react';

/**
 * ReleaseImpactHeatmap
 *
 * Compare-page calendar strip that positions each scenario's Go-Live date
 * over the Public Cloud release calendar — turning the per-estimate
 * conflict warning into a portfolio view ready for executive review.
 *
 * Layout
 *   ┌──────────────────────── time axis ────────────────────────┐
 *   │ rel events (Test/Prod/Maint colour bands at exact dates)  │
 *   ├───────────────────────────────────────────────────────────┤
 *   │ ▲ scenario A — Go-Live (severity ring)                    │
 *   │ ▲ scenario B — Go-Live                                    │
 *   │ ▲ scenario C — Go-Live                                    │
 *   └───────────────────────────────────────────────────────────┘
 *
 * Severity matches ReleaseConflictCallout:
 *   maintenance ±7d → CRITICAL, prod ±14d → WARNING, test ±7d → INFO
 *
 * Props
 *   scenarios: [{ id, name, go_live_date, is_winner }]
 *   releases:  [{ id, quarter, release_type, date, label }]
 *   onLockInWinner?: (scenarioId, scenarioName) => void
 *     If provided, each non-winner row exposes a "Lock in as Winner"
 *     action that opens the parent's nominate dialog pre-selected with
 *     this scenario.
 */
const TYPE_COLORS = {
  test: { band: 'bg-cyan-500/20', edge: 'bg-cyan-500', label: 'Test' },
  prod: { band: 'bg-amber-400/25', edge: 'bg-amber-500', label: 'Prod' },
  maintenance: { band: 'bg-red-500/20', edge: 'bg-red-500', label: 'Maintenance' },
};

const SEVERITY_RING = {
  critical: { ring: 'ring-2 ring-red-500', dot: 'bg-red-600', icon: AlertCircle, label: 'Critical' },
  warning: { ring: 'ring-2 ring-amber-500', dot: 'bg-amber-500', icon: AlertTriangle, label: 'Warning' },
  info: { ring: 'ring-2 ring-cyan-500', dot: 'bg-cyan-500', icon: Info, label: 'Info' },
  clear: { ring: 'ring-1 ring-emerald-400/70', dot: 'bg-emerald-500', icon: null, label: 'Clear' },
};

function parseIso(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return isNaN(d) ? null : d;
}

function fmtShort(iso) {
  const d = parseIso(iso);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: '2-digit' });
}

function daysBetween(aIso, bIso) {
  const a = parseIso(aIso); const b = parseIso(bIso);
  if (!a || !b) return null;
  return Math.round((a - b) / 86400000);
}

function severityFor(goLive, releases) {
  if (!goLive || !releases?.length) return 'clear';
  let worst = 'clear';
  const order = { clear: 0, info: 1, warning: 2, critical: 3 };
  for (const r of releases) {
    const diff = daysBetween(goLive, r.date);
    if (diff === null) continue;
    const abs = Math.abs(diff);
    let sev = null;
    if (r.release_type === 'maintenance' && abs <= 7) sev = 'critical';
    else if (r.release_type === 'prod' && abs <= 14) sev = 'warning';
    else if (r.release_type === 'test' && abs <= 7) sev = 'info';
    if (sev && order[sev] > order[worst]) worst = sev;
  }
  return worst;
}

export default function ReleaseImpactHeatmap({ scenarios, releases, onLockInWinner }) {
  const data = useMemo(() => {
    const withDates = (scenarios || []).filter(s => s.go_live_date);
    if (!withDates.length) return null;

    // Domain: 30d before earliest reference → 30d after latest reference,
    // pulling in any release events that fall inside the window so PMs see
    // their cluster context.
    const refDates = [
      ...withDates.map(s => parseIso(s.go_live_date)).filter(Boolean),
      ...((releases || []).map(r => parseIso(r.date)).filter(Boolean)),
    ];
    if (!refDates.length) return null;

    const min = new Date(Math.min(...refDates.map(d => d.getTime())));
    const max = new Date(Math.max(...refDates.map(d => d.getTime())));
    const padDays = 14;
    const start = new Date(min.getTime() - padDays * 86400000);
    const end = new Date(max.getTime() + padDays * 86400000);
    const span = Math.max(1, (end - start) / 86400000);

    const pct = (iso) => {
      const d = parseIso(iso);
      if (!d) return null;
      return ((d - start) / 86400000 / span) * 100;
    };

    return {
      start, end, span,
      scenarios: withDates.map(s => ({
        ...s,
        leftPct: pct(s.go_live_date),
        severity: severityFor(s.go_live_date, releases || []),
      })),
      releases: (releases || [])
        .filter(r => r.date && parseIso(r.date) >= start && parseIso(r.date) <= end)
        .map(r => ({ ...r, leftPct: pct(r.date) })),
    };
  }, [scenarios, releases]);

  if (!data) return null;

  // Month tick rule — bigger spans use quarterly ticks.
  const months = [];
  const cursor = new Date(data.start.getFullYear(), data.start.getMonth(), 1);
  const daysSpan = data.span;
  const step = daysSpan > 365 ? 3 : daysSpan > 180 ? 2 : 1;
  while (cursor <= data.end) {
    const left = ((cursor - data.start) / 86400000 / data.span) * 100;
    if (left >= 0 && left <= 100) {
      months.push({
        left,
        label: cursor.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      });
    }
    cursor.setMonth(cursor.getMonth() + step);
  }

  // Aggregate severity counts for the header chip.
  const severityCounts = data.scenarios.reduce((acc, s) => {
    acc[s.severity] = (acc[s.severity] || 0) + 1;
    return acc;
  }, {});

  return (
    <Card data-testid="release-impact-heatmap-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <CalendarRange className="w-4 h-4 text-slate-500" />
          Release Impact Heatmap
          <span className="ml-auto flex items-center gap-1.5 text-[10px] font-normal">
            {Object.entries(severityCounts)
              .filter(([sev]) => sev !== 'clear')
              .map(([sev, n]) => {
                const cfg = SEVERITY_RING[sev];
                return (
                  <span
                    key={sev}
                    className={`px-1.5 py-0.5 rounded-full text-white ${cfg.dot}`}
                    data-testid={`heatmap-severity-count-${sev}`}
                  >
                    {n} {cfg.label}
                  </span>
                );
              })}
            {severityCounts.clear > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-white bg-emerald-500" data-testid="heatmap-severity-count-clear">
                {severityCounts.clear} Clear
              </span>
            )}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-3">
        <p className="text-[11px] text-slate-500 leading-snug">
          Each row plots a scenario's Go-Live date against the Public Cloud release calendar. Coloured
          bands show release windows; rings around the marker indicate severity (red = Maintenance ±7d,
          amber = Prod ±14d, cyan = Test ±7d, green = clear).
        </p>

        {/* Month-axis labels */}
        <div className="relative h-5" data-testid="heatmap-month-axis">
          {months.map((m, i) => (
            <span
              key={i}
              className="absolute -translate-x-1/2 text-[10px] text-slate-500 font-medium whitespace-nowrap"
              style={{ left: `${m.left}%` }}
            >
              {m.label}
            </span>
          ))}
        </div>

        {/* Release-event lane */}
        <div className="relative h-7 bg-slate-50 rounded-md border border-slate-200 overflow-hidden" data-testid="heatmap-release-lane">
          {data.releases.map((r, i) => {
            const cfg = TYPE_COLORS[r.release_type] || TYPE_COLORS.test;
            // ±7d soft band so PMs see the danger zone, not just the spike.
            const bandWidthPct = (14 / data.span) * 100;
            return (
              <React.Fragment key={`${r.id || r.date}-${i}`}>
                <div
                  className={`absolute top-0 h-full ${cfg.band}`}
                  style={{
                    left: `${Math.max(0, r.leftPct - bandWidthPct / 2)}%`,
                    width: `${Math.min(100, bandWidthPct)}%`,
                  }}
                  title={`${r.label || r.quarter || cfg.label} window (±7d)`}
                />
                <div
                  className={`absolute top-0 h-full w-[2px] ${cfg.edge}`}
                  style={{ left: `calc(${r.leftPct}% - 1px)` }}
                  title={`${r.label || r.quarter || cfg.label} — ${fmtShort(r.date)}`}
                  data-testid={`heatmap-release-marker-${r.release_type}-${i}`}
                />
              </React.Fragment>
            );
          })}
          {!data.releases.length && (
            <div className="absolute inset-0 flex items-center justify-center text-[10px] text-slate-400 italic">
              No release events configured for this date range.
            </div>
          )}
        </div>

        {/* Scenario rows */}
        <div className="space-y-1.5" data-testid="heatmap-scenario-rows">
          {data.scenarios.map((s, i) => {
            const cfg = SEVERITY_RING[s.severity];
            const Icon = cfg.icon;
            return (
              <div
                key={s.id}
                className="relative h-9 group"
                data-testid={`heatmap-scenario-row-${i}`}
                data-severity={s.severity}
              >
                {/* Faint backdrop track so empty rows still read as a row. */}
                <div className="absolute inset-y-1.5 inset-x-0 bg-slate-50/60 rounded" />
                {/* Scenario name pill on the left */}
                <div className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-1.5 z-10 bg-white/90 backdrop-blur-sm px-1.5 py-0.5 rounded text-[11px] font-medium text-slate-700 max-w-[40%] truncate">
                  {s.is_winner && <Trophy className="w-3 h-3 text-amber-600 shrink-0" />}
                  <span className="truncate" title={s.name}>{s.name}</span>
                </div>
                {/* Marker */}
                <div
                  className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 rounded-full ${cfg.dot} ${cfg.ring} ring-offset-1 ring-offset-white shadow-sm group-hover:scale-125 transition-transform`}
                  style={{ left: `${s.leftPct}%` }}
                  data-testid={`heatmap-scenario-marker-${i}`}
                  title={`${s.name} — Go-Live ${fmtShort(s.go_live_date)} (${cfg.label})`}
                >
                  {Icon && <Icon className="w-2.5 h-2.5 text-white absolute inset-0 m-auto" />}
                </div>
                {/* Date label trailing the marker */}
                <span
                  className="absolute top-1/2 -translate-y-1/2 ml-3 text-[10px] font-medium text-slate-600 whitespace-nowrap"
                  style={{ left: `${s.leftPct}%` }}
                >
                  {fmtShort(s.go_live_date)}
                </span>
                {/* Lock-in-as-Winner action — pinned to the right edge,
                    revealed on row hover so the timeline reads cleanly
                    at rest. Skipped for the current winner (no-op) and
                    when the parent didn't supply the callback. */}
                {onLockInWinner && !s.is_winner && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onLockInWinner(s.id, s.name);
                    }}
                    className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-amber-100 z-20"
                    data-testid={`heatmap-lock-winner-${i}`}
                    title={`Lock in "${s.name}" as Winner — opens the nomination dialog with this scenario pre-selected`}
                  >
                    <Crown className="w-3 h-3" />
                    Lock in as Winner
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-slate-100" data-testid="heatmap-legend">
          <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Legend</span>
          {Object.entries(TYPE_COLORS).map(([type, cfg]) => (
            <span key={type} className="flex items-center gap-1 text-[10px] text-slate-600">
              <span className={`w-3 h-2 rounded-sm ${cfg.band}`} />
              <span className={`w-[2px] h-2 ${cfg.edge}`} />
              {cfg.label}
            </span>
          ))}
          <span className="mx-2 h-3 w-px bg-slate-200" />
          {Object.entries(SEVERITY_RING).map(([sev, cfg]) => (
            <span key={sev} className="flex items-center gap-1 text-[10px] text-slate-600">
              <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot} ${cfg.ring} ring-offset-1 ring-offset-white`} />
              {cfg.label}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
